/**
 * <loading-screen>
 *
 * The sitewide arrival moment: a full viewport of the chosen colour scheme —
 * optionally under the tiled accent pattern — on which the Dogwood badge draws
 * itself on layer by layer before the whole screen fades away.
 *
 * Rendered by snippets/loading-screen.liquid. Read that file first — it owns the
 * decision about WHETHER the screen appears at all, and it makes that decision
 * before first paint. By the time this component runs, the answer is already
 * yes.
 *
 * WHAT THIS FILE OWNS: fetching and injecting the badge, running the timeline,
 * and cleaning up after itself. Everything visual is CSS in the snippet.
 *
 * IT HAS ONE OUTWARD SIGNAL: a `dogwood:loading-screen:done` event on `document`,
 * fired the instant before the element removes itself. Added for the newsletter
 * popup, which must not open while the splash is still on screen. Anything else
 * that needs to wait for the arrival moment should listen for it rather than
 * poll for the element's absence — this runs during the most frame-sensitive
 * 2.7s of the page and has no budget for a spare interval.
 *
 * 🚨 THE SCREEN IS NEVER ALLOWED TO OUTLIVE ITS BUDGET. Nothing on the page is
 * waiting for the badge, so a slow or failed fetch must not hold the storefront
 * hostage. The exit is scheduled against a hard ceiling (MAX_TOTAL_MS) measured
 * from the moment this element connects, whatever the network does. On a cold
 * cache the badge simply arrives partway through and the later layers still get
 * their reveal; on a failure the screen fades out over a plain coloured
 * background, which reads as intentional rather than broken.
 *
 * Deliberately NOT here: a page scroll lock. The existing sitewide lock in
 * critical.css is keyed on `dialog[scroll-lock][open]`, and adding `overflow:
 * hidden` to <html> for 2.7s would collapse the scrollbar and jump the page
 * sideways the moment the screen is removed — on any platform with classic
 * scrollbars, that jump lands while the page is fully visible. A visible layout
 * shift is a worse outcome than someone scrolling during a splash.
 */

if (!customElements.get('loading-screen')) {
  customElements.define(
    'loading-screen',
    class LoadingScreen extends HTMLElement {
      /* 🚨 REVEAL_MS and EXIT_MS MIRROR snippets/loading-screen.liquid and cannot
         be changed here alone. REVEAL_MS is the last animation-delay plus its
         duration (860 + 440); EXIT_MS is the transition on [data-state='leaving'].
         Together they come to a ~2.7s screen on a warm cache.

         The shape of that 2.7s is deliberate and was arrived at by iteration:
         the reveal is the shortest part, the HOLD is long enough to register as
         a moment of stillness on the finished badge, and the EXIT is the
         longest single beat. It reads as arrive, settle, dissolve. Shortening
         the hold makes the badge feel like it is fleeing the moment it
         completes. */

      /** Last layer's animation-delay + duration in the snippet's CSS. */
      static REVEAL_MS = 1300;
      /** Beat of stillness on the finished badge before the screen leaves. */
      static HOLD_MS = 400;
      /** Must match the transition duration on [data-state='leaving']. */
      static EXIT_MS = 1000;
      /** Hard ceiling from connect to removal, whatever the network does. */
      static MAX_TOTAL_MS = 3600;
      static STORAGE_KEY = 'dogwood:loading-screen';

      /* 🚨 A PUBLIC CONTRACT. assets/newsletter-popup.js waits on this event so
         the popup never lands on top of the splash. Renaming it silently breaks
         that — the popup would fall through to its safety timeout instead. */
      static DONE_EVENT = 'dogwood:loading-screen:done';

      connectedCallback() {
        this.timers = [];
        this.connectedAt = performance.now();

        // Written immediately rather than on completion: a visitor who clicks
        // through before the screen finishes should not see it again on the
        // next page. Failure here is not fatal — worst case it replays.
        try {
          sessionStorage.setItem(LoadingScreen.STORAGE_KEY, 'played');
        } catch (error) {
          /* Safari private browsing throws on write. The screen still plays. */
        }

        this.art = this.querySelector('.loading-screen__art');
        this.loadBadge();

        // Worst case, booked immediately: if the badge never arrives, the screen
        // still leaves at the ceiling. startReveal() pulls this earlier once it
        // knows when the reveal actually began.
        this.scheduleExitAt(LoadingScreen.MAX_TOTAL_MS - LoadingScreen.EXIT_MS);
      }

      disconnectedCallback() {
        this.timers.forEach(clearTimeout);
        this.timers = [];
        clearTimeout(this.exitTimer);
        this.abortController?.abort();
      }

      get elapsed() {
        return performance.now() - this.connectedAt;
      }

      after(delay, fn) {
        this.timers.push(setTimeout(fn, Math.max(0, delay)));
      }

      async loadBadge() {
        const href = this.dataset.badge;
        if (!href || !this.art) return;

        this.abortController = new AbortController();

        try {
          const response = await fetch(href, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

          const markup = await response.text();

          // Parsed rather than assigned to innerHTML so a malformed or
          // unexpected response can be rejected instead of injected.
          const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
          const svg = parsed.querySelector('svg');
          if (!svg || parsed.querySelector('parsererror')) {
            throw new Error('badge asset is not a parseable SVG');
          }

          if (!this.isConnected) return;
          this.art.replaceChildren(document.importNode(svg, true));
          this.startReveal();
        } catch (error) {
          if (error.name === 'AbortError') return;
          // Non-fatal by design: the screen still runs its timeline over the
          // scheme background and pattern, and still leaves on schedule.
          console.warn('[loading-screen] badge unavailable, showing plain screen', error);
        }
      }

      startReveal() {
        // Two frames, not one. The first lets the injected <svg> be laid out;
        // the second guarantees the browser has taken a style snapshot with the
        // layers still at opacity 0, so the animation has something to animate
        // FROM. With a single frame the first layer can pop in fully formed.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!this.isConnected) return;
            this.dataset.state = 'revealing';

            // Now that the reveal has a real start time, bring the exit back in
            // to sit just after the last layer settles — but never past the
            // ceiling already booked in connectedCallback. On a warm cache this
            // resolves to ~1.7s; on a slow one the ceiling wins and the last
            // layers are cut short, which is the correct trade.
            const { REVEAL_MS, HOLD_MS, EXIT_MS, MAX_TOTAL_MS } = LoadingScreen;
            this.scheduleExitAt(
              Math.min(this.elapsed + REVEAL_MS + HOLD_MS, MAX_TOTAL_MS - EXIT_MS)
            );
          });
        });
      }

      /** @param {number} at - ms since connect at which the fade-out begins. */
      scheduleExitAt(at) {
        clearTimeout(this.exitTimer);
        this.exitTimer = setTimeout(() => {
          this.dataset.state = 'leaving';
          this.after(LoadingScreen.EXIT_MS, () => {
            /* Fired BEFORE removal rather than after, so a listener reading the
               DOM in response still sees a consistent page. Anything that must
               not appear during the splash waits on this. */
            document.dispatchEvent(new CustomEvent(LoadingScreen.DONE_EVENT));
            this.remove();
          });
        }, Math.max(0, at - this.elapsed));
      }
    }
  );
}
